import React, { useEffect, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { MapControls, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// Kamera-Controller Komponente für View-Mode-Wechsel
function CameraController({ viewMode, isDragging }) {
  const { camera } = useThree();
  const initialized = useRef(false);

  useEffect(() => {
    if (viewMode === '3d') {
      // 3D-Ansicht: Isometrische Perspektive
      camera.position.set(15000, -15000, 12000);
      camera.up.set(0, 0, 1);
      camera.lookAt(0, 0, 0);
      camera.zoom = 0.08;
    } else {
      // 2D-Ansicht: Top-Down
      camera.position.set(0, 0, 100);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 0, 0);
      // Zoom nur beim ersten Mal setzen, sonst bleibt der User-Zoom erhalten
      if (!initialized.current) {
        camera.zoom = 0.15;
      }
    }
    camera.updateProjectionMatrix();
    initialized.current = true;
  }, [viewMode, camera]);

  // Unterschiedliche Controls basierend auf View-Mode
  if (viewMode === '3d') {
    return (
      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2.2} // Nicht unter die Grundebene
        minZoom={0.01}
        maxZoom={2}
        enabled={!isDragging}
        target={[0, 0, 0]}
      />
    );
  }

  // In 2D: Linke Maustaste ist für Selektion reserviert
  // Mittlere/Rechte Maustaste = Verschieben, Mausrad = Zoom
  return (
    <MapControls
      enableRotate={false}
      screenSpacePanning={true}
      minZoom={0.01}
      maxZoom={5}
      enabled={!isDragging}
      mouseButtons={{
        LEFT: null, // Linke Maustaste deaktiviert (für Selektion)
        MIDDLE: THREE.MOUSE.PAN,
        RIGHT: THREE.MOUSE.PAN,
      }}
    />
  );
}

export function Canvas3D({ children, onPointerMissed, isDragging = false, viewMode = '2d' }) {
  return (
    <Canvas
      orthographic
      camera={{
        position: viewMode === '3d' ? [15000, -15000, 12000] : [0, 0, 100],
        zoom: viewMode === '3d' ? 0.08 : 0.15,
        near: 0.1,
        far: 100000,
        up: viewMode === '3d' ? [0, 0, 1] : [0, 1, 0],
      }}
      gl={{ antialias: true }}
      onPointerMissed={onPointerMissed}
      style={{ background: '#f8fafc', width: '100%', height: '100%' }}
    >
      {/* Kamera-Controller für View-Mode */}
      <CameraController viewMode={viewMode} isDragging={isDragging} />

      {/* Beleuchtung */}
      <ambientLight intensity={0.7} />
      {viewMode === '3d' && (
        <>
          <directionalLight position={[10000, 10000, 10000]} intensity={0.5} />
          <directionalLight position={[-5000, -5000, 5000]} intensity={0.3} />
        </>
      )}

      {/* Inhalt */}
      {children}
    </Canvas>
  );
}
